const LOS_ANGELES_TIME_ZONE = 'America/Los_Angeles';
const SENSITIVE_FIELD_PATTERN = /(^|_)(password|passwd|passcode|token|secret|cookie|session|credential|private_key|privatekey|raw_claims|claims|recovery_code|api_key)($|_)/i;

const ENTITY_LABELS = {
  en: {
    container: 'Container',
    shipment: 'Shipment',
    customer: 'Customer',
    driver: 'Driver',
    user: 'User',
    dispatch_task: 'Task',
    terminal: 'Terminal',
    charge: 'Charge',
    setting: 'Setting',
  },
  'zh-CN': {
    container: '集装箱',
    shipment: '运输单',
    customer: '客户',
    driver: '司机',
    user: '用户',
    dispatch_task: '任务',
    terminal: '码头',
    charge: '费用',
    setting: '设置',
  },
};

const ROLE_LABELS = {
  en: {
    admin: 'Admin',
    dispatcher: 'Dispatcher',
    worker: 'Worker',
    driver: 'Driver',
    customer: 'Customer',
    accounting: 'Accounting',
    viewer: 'Viewer',
  },
  'zh-CN': {
    admin: '管理员',
    dispatcher: '调度员',
    worker: '工作人员',
    driver: '司机',
    customer: '客户',
    accounting: '财务',
    viewer: '只读用户',
  },
};

const DISPATCH_CANCELLATION_REASON_LABELS = {
  driver_unavailable: ['Driver unavailable', '司机无法执行任务'],
  tractor_or_chassis_unavailable: ['Tractor or chassis unavailable', '牵引车或底盘无法使用'],
  container_not_available: ['Container not available', '货柜尚不可提取'],
  appointment_or_schedule_changed: ['Appointment or schedule changed', '预约或计划时间变更'],
  terminal_or_route_disruption: ['Terminal or route disruption', '码头或运输路线受阻'],
  customer_cancelled_leg: ['Customer cancelled this transport leg', '顾客取消本次运输段'],
  dispatch_correction: ['Dispatch record correction', '派车记录纠错'],
  operational_replan: ['Internal dispatch replan', '内部调度重排'],
  other: ['Other reason', '其他原因'],
};

function normalizedLocale(locale) {
  return String(locale || '').toLowerCase().startsWith('zh') ? 'zh-CN' : 'en';
}

function normalizedFieldName(value) {
  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .toLowerCase();
}

function isSensitiveField(fieldName) {
  return SENSITIVE_FIELD_PATTERN.test(normalizedFieldName(fieldName));
}

export function redactForDisplay(value) {
  if (Array.isArray(value)) return value.map((item) => redactForDisplay(item));
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [
    key,
    isSensitiveField(key) ? '[REDACTED]' : redactForDisplay(item),
  ]));
}

function dateFromAuditValue(value) {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (value && typeof value.toDate === 'function') return value.toDate();
  if (value && typeof value.toMillis === 'function') return new Date(value.toMillis());
  if (value && Number.isFinite(value.millis)) return new Date(value.millis);
  if (value && Number.isFinite(value.seconds)) return new Date(value.seconds * 1000);
  if (value?.__type === 'timestamp' && Number.isFinite(value.millis)) return new Date(value.millis);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
}

export function formatAuditTime(value, locale = 'en') {
  const date = dateFromAuditValue(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  const normalized = normalizedLocale(locale);
  return new Intl.DateTimeFormat(normalized === 'en' ? 'en-US' : 'zh-CN', {
    timeZone: LOS_ANGELES_TIME_ZONE,
    year: 'numeric',
    month: normalized === 'en' ? 'short' : 'long',
    day: 'numeric',
    // 24-hour in both locales (Alan, 2026-08-11). English used to get hour12,
    // which is what made the audit log the one place in the product reading
    // "3:05:12 PM" while everything beside it read 15:05. The seconds stay:
    // here the exact instant is the record.
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(date);
}

function zonedParts(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: LOS_ANGELES_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  return Object.fromEntries(parts
    .filter((part) => part.type !== 'literal')
    .map((part) => [part.type, Number(part.value)]));
}

function partsAsUtcMillis(parts) {
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}

export function toLosAngelesBoundary(dateValue, timeValue) {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateValue || ''));
  const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(timeValue || ''));
  if (!dateMatch || !timeMatch) throw new Error('A valid date and time are required.');

  const target = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
    second: Number(timeMatch[3] || 0),
  };
  const targetMillis = partsAsUtcMillis(target);
  const calendarCheck = new Date(targetMillis);
  if (
    calendarCheck.getUTCFullYear() !== target.year
    || calendarCheck.getUTCMonth() + 1 !== target.month
    || calendarCheck.getUTCDate() !== target.day
    || target.hour > 23
    || target.minute > 59
    || target.second > 59
  ) {
    throw new Error('A valid date and time are required.');
  }

  let guess = targetMillis;
  for (let attempt = 0; attempt < 6; attempt += 1) {
    const actual = zonedParts(new Date(guess));
    const delta = targetMillis - partsAsUtcMillis(actual);
    if (delta === 0) break;
    guess += delta;
  }

  const verified = zonedParts(new Date(guess));
  if (Object.keys(target).some((key) => verified[key] !== target[key])) {
    throw new Error('The selected local time does not exist in America/Los_Angeles.');
  }
  return guess;
}

function timestampPartsFromLog(log) {
  const value = log?.performed_at ?? log?.created_at;
  if (Number.isFinite(value?.seconds)) {
    return {
      seconds: Number(value.seconds),
      nanoseconds: Number.isFinite(value.nanoseconds) ? Number(value.nanoseconds) : 0,
    };
  }
  const date = dateFromAuditValue(value);
  const millis = date ? date.getTime() : 0;
  return {
    seconds: Math.floor(millis / 1000),
    nanoseconds: ((millis % 1000) + 1000) % 1000 * 1_000_000,
  };
}

export function auditBoundary(log) {
  return {
    ...timestampPartsFromLog(log),
    id: String(log?.id || log?.event_sequence || ''),
  };
}

export function compareFirestoreDocumentIds(left, right) {
  const leftId = String(left ?? '');
  const rightId = String(right ?? '');
  if (leftId === rightId) return 0;
  return leftId < rightId ? -1 : 1;
}

export function compareAuditBoundariesDesc(left, right) {
  if (left.seconds !== right.seconds) return left.seconds > right.seconds ? -1 : 1;
  if (left.nanoseconds !== right.nanoseconds) return left.nanoseconds > right.nanoseconds ? -1 : 1;
  return compareFirestoreDocumentIds(right.id, left.id);
}

export function isVisibleBusinessAudit(log) {
  if (log?.is_system_actor === true || log?.actor_type === 'system') return false;
  const actorIdentity = String(
    log?.actor_uid
    || log?.actor_name
    || log?.actor_email
    || log?.performed_by?.path
    || log?.performed_by
    || '',
  ).toLowerCase();
  if (actorIdentity === 'system' || actorIdentity === 'users/system' || actorIdentity.endsWith('/system')) {
    return false;
  }
  return true;
}

export function auditActorLabel(log, locale = 'en') {
  const normalized = normalizedLocale(locale);
  const name = String(log?.actor_name || log?.actor_email || 'Unknown User');
  const roleKey = String(log?.actor_role || '').toLowerCase();
  const role = ROLE_LABELS[normalized][roleKey] || (normalized === 'en' ? 'Unknown Role' : '未知角色');
  return `${name} - ${role}`;
}

function entityType(log) {
  return String(log?.entity_type || log?.target_collection || 'item').replace(/s$/, '');
}

export function identifier(log) {
  return String(log?.entity_identifier || log?.target_doc_id || log?.entity_ref?.id || 'Unknown');
}

export function changedItemLabel(log, locale = 'en') {
  const normalized = normalizedLocale(locale);
  const type = entityType(log);
  const label = ENTITY_LABELS[normalized][type] || (normalized === 'en' ? 'Item' : '项目');
  return `${label} - ${identifier(log)}`;
}

function fieldChanges(log) {
  if (log?.field_changes && typeof log.field_changes === 'object') return log.field_changes;
  const before = redactForDisplay(log?.before_data ?? log?.old_value ?? {});
  const after = redactForDisplay(log?.after_data ?? log?.new_value ?? {});
  const fields = Array.isArray(log?.changed_fields)
    ? log.changed_fields
    : [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return Object.fromEntries(fields.map((fieldPath) => {
    const beforeExists = Object.prototype.hasOwnProperty.call(before, fieldPath);
    const afterExists = Object.prototype.hasOwnProperty.call(after, fieldPath);
    return [fieldPath, {
      before: beforeExists ? before[fieldPath] : { __type: 'missing' },
      after: afterExists ? after[fieldPath] : { __type: 'missing' },
      change_type: !beforeExists ? 'added' : !afterExists ? 'removed' : 'changed',
    }];
  }));
}

export function buildAuditSummary(log, locale = 'en') {
  const normalized = normalizedLocale(locale);
  const type = entityType(log);
  const id = identifier(log);
  const changes = fieldChanges(log);
  const status = changes.status?.after;

  if (type === 'dispatch_task' && status === 'cancelled') {
    const reasonCode = String(changes.cancel_reason_code?.after || 'other');
    const labels = DISPATCH_CANCELLATION_REASON_LABELS[reasonCode] || DISPATCH_CANCELLATION_REASON_LABELS.other;
    const reason = labels[normalized === 'en' ? 0 : 1];
    return normalized === 'en'
      ? `Dispatch task ${id} was cancelled: ${reason}.`
      : `Dispatch Task ${id} 已取消：${reason}。`;
  }

  if (status === 'picked_up_from_terminal') {
    return normalized === 'en'
      ? `Container ${id} was marked as picked up.`
      : `集装箱 ${id} 已提柜。`;
  }
  if (status === 'arrived_at_warehouse') {
    return normalized === 'en'
      ? `Container ${id} arrived at the warehouse.`
      : `集装箱 ${id} 已到仓库。`;
  }
  if (status === 'empty_returned' || status === 'empty_returned_to_terminal') {
    return normalized === 'en'
      ? `Container ${id} was returned empty.`
      : `集装箱 ${id} 已还空。`;
  }
  if (changes.assigned_driver_name?.after) {
    const driver = String(changes.assigned_driver_name.after);
    return normalized === 'en'
      ? `Container ${id} was assigned to ${driver}.`
      : `集装箱 ${id} 已分配给 ${driver}。`;
  }

  const operation = String(log?.operation_type || log?.action || 'updated');
  const label = ENTITY_LABELS[normalized][type] || (normalized === 'en' ? 'item' : '项目');
  if (operation === 'created') {
    return normalized === 'en'
      ? `New ${label.toLowerCase()} ${id} was added.`
      : `新${label} ${id} 已添加。`;
  }
  if (operation === 'deleted') {
    return normalized === 'en'
      ? `${label} ${id} was removed.`
      : `${label} ${id} 已移除。`;
  }
  return normalized === 'en'
    ? `${label} ${id} was updated.`
    : `${label} ${id} 已更新。`;
}

function displayValue(fieldPath, value, locale) {
  if (value && typeof value === 'object' && value.__type === 'missing') {
    return normalizedLocale(locale) === 'en' ? 'Missing field' : '字段不存在';
  }
  if (value && typeof value === 'object' && value.__type === 'redacted') return '[REDACTED]';
  if (value === '[REDACTED]') return value;
  if (value === null) return 'null';
  if (fieldPath.endsWith('_cents') && Number.isFinite(value)) {
    return new Intl.NumberFormat(normalizedLocale(locale) === 'en' ? 'en-US' : 'zh-CN', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);
  }
  const date = dateFromAuditValue(value);
  if (date && (value?.__type === 'timestamp' || typeof value?.toDate === 'function')) {
    return formatAuditTime(date, locale);
  }
  if (typeof value === 'object') return JSON.stringify(redactForDisplay(value));
  return String(value);
}

export function describeFieldChanges(log, locale = 'en') {
  const normalized = normalizedLocale(locale);
  const labels = normalized === 'en'
    ? { added: 'Added', removed: 'Removed', changed: 'Changed' }
    : { added: '已添加', removed: '已移除', changed: '已更改' };
  return Object.entries(fieldChanges(log))
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([fieldPath, change]) => ({
      fieldPath,
      changeType: labels[change.change_type] || labels.changed,
      before: displayValue(fieldPath, change.before, locale),
      after: displayValue(fieldPath, change.after, locale),
    }));
}
